// frontend/super-admin/src/pages/PaymentSettings.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  message,
  Alert,
  Space,
  Image,
  Divider,
  Typography
} from 'antd';
import {
  UploadOutlined,
  SaveOutlined,
  QrcodeOutlined,
  CopyOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { TextArea } = Input;
const { Title, Text } = Typography;

export default function PaymentSettings() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentQR, setCurrentQR] = useState(null);
  const [newQRFile, setNewQRFile] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/settings/payment');
      const data = response.data.data;

      form.setFieldsValue({
        upiId: data.upiId,
        upiPhone: data.upiPhone,
        instructions: data.instructions
      });

      if (data.upiQRCode) {
        setCurrentQR(data.upiQRCode);
      }
    } catch (error) {
      message.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleQRUpload = (info) => {
    if (info.file.status !== 'uploading') {
      setNewQRFile(info.file.originFileObj || info.file);
      message.success('QR Code selected');
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      setSaving(true);

      const values = form.getFieldsValue();
      const formData = new FormData();

      formData.append('upiId', values.upiId || '');
      formData.append('upiPhone', values.upiPhone || '');
      formData.append('instructions', values.instructions || '');

      if (newQRFile) {
        formData.append('qrCode', newQRFile);
      }

      await api.post('/api/admin/settings/payment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      message.success('✅ Payment settings saved successfully!');
      setNewQRFile(null);
      fetchSettings();

    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUPI = () => {
    const upiId = form.getFieldValue('upiId');
    if (upiId) {
      navigator.clipboard.writeText(upiId);
      message.success('UPI ID copied to clipboard!');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>💰 Payment Settings</h1>
        <p style={{ color: '#999', margin: '4px 0 0 0' }}>
          Configure UPI payment details for tenant subscriptions
        </p>
      </div>

      <Alert
        message="📌 Important"
        description="These payment details will be shown to tenants when they upgrade their plans. Make sure all information is accurate."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          {/* UPI QR Code */}
          <Title level={5}>
            <QrcodeOutlined style={{ marginRight: 8 }} />
            UPI QR Code
          </Title>

          <div style={{ marginBottom: 24 }}>
            {currentQR && !newQRFile && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Current QR Code:
                </Text>
                <Image
                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/payment/${currentQR.split('/').pop()}`}
                  alt="Current UPI QR Code"
                  style={{ 
                    maxWidth: 200,
                    border: '2px solid #d9d9d9',
                    borderRadius: 8,
                    padding: 8
                  }}
                />
              </div>
            )}

            <Upload
              accept="image/*"
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleQRUpload}
              listType="picture-card"
              showUploadList={!!newQRFile}
            >
              <div>
                <UploadOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  {currentQR ? 'Change QR Code' : 'Upload QR Code'}
                </div>
              </div>
            </Upload>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Max 2MB • JPG or PNG • Recommended: 512x512px
            </Text>
          </div>

          <Divider />

          {/* UPI ID */}
          <Title level={5}>UPI Details</Title>

          <Form.Item
            name="upiId"
            label="UPI ID"
            rules={[
              { required: true, message: 'Please enter UPI ID' },
              { pattern: /^[\w.-]+@[\w.-]+$/, message: 'Invalid UPI ID format (e.g., merchant@paytm)' }
            ]}
          >
            <Input
              placeholder="e.g., merchant@paytm"
              size="large"
              suffix={
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={handleCopyUPI}
                  size="small"
                />
              }
            />
          </Form.Item>

          <Form.Item
            name="upiPhone"
            label="UPI Registered Phone Number"
            rules={[
              { pattern: /^[0-9]{10}$/, message: 'Enter valid 10-digit phone number' }
            ]}
          >
            <Input
              placeholder="e.g., 9876543210"
              size="large"
              maxLength={10}
            />
          </Form.Item>

          <Divider />

          {/* Payment Instructions */}
          <Title level={5}>Payment Instructions</Title>

          <Form.Item
            name="instructions"
            label="Instructions for Users"
            extra="This message will be shown to users in the payment modal"
          >
            <TextArea
              rows={4}
              placeholder="e.g., Please complete the payment and upload screenshot with transaction ID. Your plan will be activated after verification."
            />
          </Form.Item>

          {/* Action Buttons */}
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Save Settings
              </Button>

              <Button
                onClick={fetchSettings}
                size="large"
              >
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Preview Card */}
      <Card 
        title="📱 Preview - How Tenants Will See" 
        style={{ marginTop: 24 }}
        bodyStyle={{ background: '#fafafa' }}
      >
        <div style={{ maxWidth: 400, margin: '0 auto', background: '#fff', padding: 24, borderRadius: 8 }}>
          <Title level={5} style={{ textAlign: 'center', marginBottom: 16 }}>
            Pay via UPI
          </Title>

          {currentQR && (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Image
                src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/payment/${currentQR.split('/').pop()}`}
                alt="Preview QR"
                style={{ 
                  maxWidth: 150,
                  border: '1px solid #d9d9d9',
                  borderRadius: 4
                }}
                preview={false}
              />
            </div>
          )}

          <div style={{ background: '#f0f0f0', padding: 12, borderRadius: 6, marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>UPI ID:</Text>
            <div>
              <Text strong>{form.getFieldValue('upiId') || 'merchant@paytm'}</Text>
            </div>
          </div>

          {form.getFieldValue('upiPhone') && (
            <div style={{ background: '#f0f0f0', padding: 12, borderRadius: 6, marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Phone:</Text>
              <div>
                <Text strong>{form.getFieldValue('upiPhone')}</Text>
              </div>
            </div>
          )}

          {form.getFieldValue('instructions') && (
            <Alert
              message={form.getFieldValue('instructions')}
              type="info"
              showIcon
              style={{ fontSize: 12 }}
            />
          )}
        </div>
      </Card>
    </div>
  );
}